import React, { useState, useEffect } from 'react';
import { Card, CardContent } from './ui/card';
import { Star, Quote, ChevronLeft, ChevronRight } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Sample testimonials data
const sampleTestimonials = [
  {
    id: '1',
    client_name: 'Michael & Sarah T.',
    client_location: 'Winnipeg, MB',
    service_type: 'mortgage',
    rating: 5,
    testimonial_text: 'Justin and the team at Utmost Financial made our first home purchase so smooth. They found us an amazing rate and guided us through every step. Highly recommend!',
    published: true
  },
  {
    id: '2',
    client_name: 'David L.',
    client_location: 'Edmonton, AB',
    service_type: 'insurance',
    rating: 5,
    testimonial_text: 'I was looking for life insurance to protect my family. Norman explained all my options clearly and helped me choose the perfect coverage. Excellent service!',
    published: true
  },
  {
    id: '3',
    client_name: 'Jennifer M.',
    client_location: 'Vancouver, BC',
    service_type: 'mortgage',
    rating: 5,
    testimonial_text: 'Linda helped us refinance our mortgage and we\'re saving hundreds every month. The process was quick and stress-free. Thank you, Utmost Financial!',
    published: true
  },
  {
    id: '4',
    client_name: 'Robert & Ana P.',
    client_location: 'Winnipeg, MB',
    service_type: 'investment',
    rating: 5,
    testimonial_text: 'Great team! They helped us set up RESPs for our kids and an investment plan for retirement. Very professional and knowledgeable.',
    published: true
  },
  {
    id: '5',
    client_name: 'Karen W.',
    client_location: 'Calgary, AB',
    service_type: 'insurance',
    rating: 5,
    testimonial_text: 'After a health scare, I realized I needed critical illness insurance. The team found me coverage even with my pre-existing conditions. Forever grateful!',
    published: true
  }
];

const Testimonials = ({ showTitle = true, maxItems = 3 }) => {
  const [testimonials, setTestimonials] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchTestimonials();
  }, []);

  const fetchTestimonials = async () => {
    try {
      const response = await fetch(`${API_URL}/api/testimonials`);
      if (response.ok) {
        const data = await response.json();
        setTestimonials(data.length > 0 ? data : sampleTestimonials);
      } else {
        setTestimonials(sampleTestimonials);
      }
    } catch (error) {
      console.error('Error fetching testimonials:', error);
      setTestimonials(sampleTestimonials);
    } finally {
      setLoading(false);
    }
  };

  const nextSlide = () => {
    setCurrentIndex((prev) => 
      prev === testimonials.length - maxItems ? 0 : prev + 1
    );
  };

  const prevSlide = () => {
    setCurrentIndex((prev) => 
      prev === 0 ? testimonials.length - maxItems : prev - 1
    );
  };

  const getServiceLabel = (type) => {
    const labels = {
      mortgage: 'Mortgage Services',
      insurance: 'Insurance',
      investment: 'Investments'
    };
    return labels[type] || type;
  };

  const getServiceColor = (type) => {
    const colors = {
      mortgage: 'text-green-600',
      insurance: 'text-blue-600',
      investment: 'text-purple-600'
    };
    return colors[type] || 'text-gray-600';
  };

  if (loading) {
    return (
      <div className="py-16 bg-[#F0F7FF]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 text-center">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-200 rounded w-48 mx-auto mb-4"></div>
            <div className="h-4 bg-gray-200 rounded w-64 mx-auto"></div>
          </div>
        </div>
      </div>
    );
  }

  const visibleTestimonials = testimonials.slice(currentIndex, currentIndex + maxItems);

  return (
    <section className="py-16 bg-[#F0F7FF]">
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        {showTitle && (
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-800 mb-3">What Our Clients Say</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Don't just take our word for it - hear from some of our satisfied clients
            </p>
            <div className="w-24 h-1 bg-[#D4AF37] mx-auto mt-4"></div>
          </div>
        )}

        <div className="relative">
          {/* Navigation Buttons */}
          {testimonials.length > maxItems && (
            <>
              <button
                onClick={prevSlide}
                className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 z-10 w-10 h-10 bg-white rounded-full shadow-lg flex items-center justify-center text-gray-600 hover:text-[#0077B6] transition-colors"
                aria-label="Previous testimonial"
              >
                <ChevronLeft className="h-6 w-6" />
              </button>
              <button
                onClick={nextSlide}
                className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 z-10 w-10 h-10 bg-white rounded-full shadow-lg flex items-center justify-center text-gray-600 hover:text-[#0077B6] transition-colors"
                aria-label="Next testimonial"
              >
                <ChevronRight className="h-6 w-6" />
              </button>
            </>
          )}

          {/* Testimonials Grid */}
          <div className="grid md:grid-cols-3 gap-6">
            {visibleTestimonials.map((testimonial) => (
              <Card 
                key={testimonial.id} 
                className="bg-white border-none shadow-lg hover:shadow-xl transition-shadow"
              >
                <CardContent className="p-6">
                  {/* Quote Icon */}
                  <div className="mb-4">
                    <Quote className="h-8 w-8 text-[#D4AF37] opacity-50" />
                  </div>

                  {/* Rating */}
                  <div className="flex mb-4">
                    {[...Array(5)].map((_, i) => (
                      <Star
                        key={i}
                        className={`h-5 w-5 ${
                          i < testimonial.rating 
                            ? 'text-[#D4AF37] fill-[#D4AF37]' 
                            : 'text-gray-300'
                        }`}
                      />
                    ))}
                  </div>

                  {/* Testimonial Text */}
                  <p className="text-gray-700 mb-6 italic leading-relaxed">
                    "{testimonial.testimonial_text}"
                  </p>

                  {/* Client Info */}
                  <div className="border-t pt-4">
                    <p className="font-semibold text-gray-800">{testimonial.client_name}</p>
                    <p className="text-sm text-gray-500">{testimonial.client_location}</p>
                    <p className={`text-sm font-medium mt-1 ${getServiceColor(testimonial.service_type)}`}>
                      {getServiceLabel(testimonial.service_type)}
                    </p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Dots Indicator */}
          {testimonials.length > maxItems && (
            <div className="flex justify-center gap-2 mt-8">
              {[...Array(testimonials.length - maxItems + 1)].map((_, i) => (
                <button
                  key={i}
                  onClick={() => setCurrentIndex(i)}
                  className={`w-3 h-3 rounded-full transition-colors ${
                    currentIndex === i ? 'bg-[#0077B6]' : 'bg-gray-300 hover:bg-gray-400'
                  }`}
                  aria-label={`Go to slide ${i + 1}`}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default Testimonials;
